import React, { useState, useRef, useCallback, useEffect } from 'react';
import '../css/App.css';
import API_BASE from "./apiBase";
import CommonPagination from './CommonPagination';
import './globalLoading';
import { clearSessionCache, createSessionCacheKey, readSessionCache, writeSessionCache } from './sessionDataCache';

/* ══════════════════════════════════════════════════════════
   CONSTANTS
══════════════════════════════════════════════════════════ */
const DATA_SOURCES = [
  { key: 'consent', label: 'Consent', icon: '📋' },
  { key: 'dsar', label: 'DSAR', icon: '📝' },
  { key: 'cookies', label: 'Cookies', icon: '🍪' },
];

const SCHEMA = {
  consent: [
    'id', 'tenant_id', 'user_id', 'action', 'created_at', 'type', 'username',
    'email', 'phone', 'dob', 'consent', 'app_name', 'user_action', 'data',
    'expiry', 'status', 'parent_name', 'parent_email', 'parent_phone',
    'id_proof_type', 'id_proof_file', 'preferred_languages', 'entity',
  ],
  dsar: [
    'id', 'tenant_id', 'full_name', 'email', 'contact_phone', 'request_type',
    'personal_data_involved', 'request_details', 'declaration_name',
    'declaration_date', 'created_at', 'status',
  ],
  cookies: [
    'id', 'tenant_id', 'user_id', 'session_id', 'consent_status', 'source_url',
    'app_name', 'preferences', 'notify_request', 'user_agent', 'ip_address', 'created_at',
  ],
};

const API_BASE_URL = typeof API_BASE !== 'undefined' ? API_BASE : '';

const OPERATORS = [
  { value: 'contains', label: 'Contains' },
  { value: 'equals', label: 'Equals' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
];

const PERM_TREE = [
  { key: 'all', label: 'All', icon: '🌐', children: [] },
  {
    key: 'consent_type', label: 'Consent Type', icon: '📋',
    children: [
      { key: 'ct_email', label: 'Email Consent', icon: '📧' },
      { key: 'ct_cookie', label: 'Cookie Consent', icon: '🍪' },
      { key: 'ct_profile', label: 'Profile Consent', icon: '👤' },
    ],
  },
  {
    key: 'entity_type', label: 'Entity Type', icon: '🏢',
    children: [
      { key: 'et_employee', label: 'Employees', icon: '👤' },
      { key: 'et_vendor', label: 'Vendors', icon: '🏭' },
      { key: 'et_partner', label: 'Partners', icon: '🤝' },
    ],
  },
  {
    key: 'dsar', label: 'DSAR', icon: '📝',
    children: [
      { key: 'dsar_access', label: 'Right to Access', icon: '👁️' },
      { key: 'dsar_deletion', label: 'Right to Deletion', icon: '🗑️' },
      { key: 'dsar_portability', label: 'Data Portability', icon: '📤' },
      { key: 'dsar_rectification', label: 'Rectification', icon: '✏️' },
    ],
  },
];

const GUIDE_STEPS = [
  { title: 'Custom report builder', body: 'Combine data from Consent, DSAR, and Cookies to build a custom report.', target: '.cr-main' },
  { title: 'Add applications', body: 'Choose which application (Consent, DSAR, Cookies) to pull data from. You can add more than one and merge them.', target: '.tour-target-add-app' },
  { title: 'Smart filters', body: 'Quickly narrow results by a specific email or tenant ID before generating the report.', target: '.tour-target-smart-filters' },
  { title: 'Report name', body: "Give your report a name so it's easy to recognize later.", target: '.cr-sb-input' },
  { title: 'Choose fields', body: 'Click here to pick which columns to include in your report, and drag to reorder them.', target: '.tour-target-fields-section' },
  { title: 'Advanced filters', body: 'Add extra field-level conditions (contains, equals, greater than, etc.) beyond the smart filters.', target: '.tour-target-filters-section' },
  { title: 'Add condition', body: 'Click here to add a new filter condition to your report.', target: '.tour-target-add-condition' },
  { title: 'Report permissions', body: 'Choose who should have access to view this report — by consent type, entity type, or DSAR category.', target: '.tour-target-perms-section' },
  { title: 'Generate Report', body: 'Once your sources, fields, and filters are set, click here to build and preview the report.', target: '.cr-btn-primary' },
  { title: 'Export options', body: 'After generating, use these buttons at the bottom to export your report as CSV or PDF.', target: '.cr-action-bar' },
];

const TYPE_MAP = {
  all: 'ALL', consent_type: 'CONSENT', entity_type: 'ENTITY', dsar: 'DSAR',
};

/* ══════════════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════════════ */
function fmtLabel(str) {
  const label = String(str).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  if (/^tenant\s*id$/i.test(label)) return 'Tenant Name';
  return label;
}

function htmlToPlainText(value) {
  if (value == null || value === '') return value;
  const html = String(value);
  if (!/<[a-z][\s\S]*>/i.test(html)) return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  } catch (e) {
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }
}

function normalizeReportRows(rows, columns) {
  const consentColumns = (columns || []).filter(column => {
    const field = String(column).split('.').pop();
    return field.toLowerCase() === 'consent';
  });
  const idColumns = (columns || []).filter(column => {
    const field = String(column).split('.').pop().toLowerCase();
    return field === 'id' || field === 'consent_id' || field === 'record_id';
  });

  return (rows || []).map(row => {
    const normalized = { ...row };
    for (const column of consentColumns) {
      if (normalized[column] != null) normalized[column] = htmlToPlainText(normalized[column]);
      const flatColumn = String(column).split('.').pop();
      if (normalized[flatColumn] != null) normalized[flatColumn] = htmlToPlainText(normalized[flatColumn]);
    }
    for (const column of idColumns) {
      const val = normalized[column];
      if (val != null && !isNaN(val) && !String(val).startsWith('CNS-') && !String(val).startsWith('CID-')) {
        normalized[column] = `CNS-${String(val).padStart(8, '0')}`;
      }
      const flatColumn = String(column).split('.').pop();
      const flatVal = normalized[flatColumn];
      if (flatVal != null && !isNaN(flatVal) && !String(flatVal).startsWith('CNS-') && !String(flatVal).startsWith('CID-')) {
        normalized[flatColumn] = `CNS-${String(flatVal).padStart(8, '0')}`;
      }
    }
    return normalized;
  });
}

function downloadReportCSV(rows, filename, columns) {
  if (!rows || !rows.length) { alert('No data to export'); return; }
  const headers = (columns && columns.length) ? columns : Object.keys(rows[0]);
  const csvLines = [headers.join(',')];
  for (const r of rows) {
    const vals = headers.map(h => {
      const v = r[h] != null ? String(r[h]) : '';
      if (v.includes(',') || v.includes('"') || v.includes('\n')) return '"' + v.replace(/"/g, '""') + '"';
      return v;
    });
    csvLines.push(vals.join(','));
  }
  const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename || 'report.csv'; a.click();
  URL.revokeObjectURL(url);
}

/* ── PDF Download using browser print ── */
function downloadReportPDF(rows, columns, reportName, filterEmail, filterTenant) {
  if (!rows || !rows.length) { alert('No data to export as PDF'); return; }
  const headers = (columns && columns.length) ? columns : Object.keys(rows[0]);

  const headerHtml = headers.map(h =>
    `<th>${fmtLabel(h.includes('.') ? h.split('.').slice(1).join('.') : h)}</th>`
  ).join('');

  const rowsHtml = rows.map((row, ri) => {
    const cells = headers.map(h => {
      const val = row[h] != null ? String(row[h]) : '—';
      return `<td title="${val.replace(/"/g, "'")}">${val.length > 60 ? val.slice(0, 60) + '…' : val}</td>`;
    }).join('');
    return `<tr class="${ri % 2 === 0 ? 'even' : 'odd'}">${cells}</tr>`;
  }).join('');

  const filterInfo = [];
  if (filterEmail) filterInfo.push(`<span class="filter-tag">📧 Email: <strong>${filterEmail}</strong></span>`);
  if (filterTenant) filterInfo.push(`<span class="filter-tag">🏢 Tenant ID: <strong>${filterTenant}</strong></span>`);

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>${reportName || 'Report'}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #1e293b; background: #fff; }
  .header { background: linear-gradient(135deg, #1e40af 0%, #2563eb 100%); color: #fff; padding: 20px 24px; }
  .header h1 { font-size: 18px; font-weight: 700; margin-bottom: 4px; }
  .header .meta { font-size: 11px; opacity: 0.85; }
  .filters { background: #f0f9ff; border-bottom: 1px solid #bae6fd; padding: 8px 24px; display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
  .filters label { font-size: 11px; color: #0369a1; font-weight: 600; }
  .filter-tag { background: #e0f2fe; border: 1px solid #7dd3fc; border-radius: 20px; padding: 3px 10px; font-size: 11px; color: #0369a1; }
  .stats { padding: 10px 24px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; display: flex; gap: 16px; }
  .stat { font-size: 11px; color: #64748b; }
  .stat strong { color: #1e293b; }
  table { width: 100%; border-collapse: collapse; margin: 0; }
  thead tr { background: #1e40af; color: #fff; }
  thead th { padding: 9px 12px; text-align: left; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; border-right: 1px solid rgba(255,255,255,0.15); white-space: nowrap; }
  tbody tr.even { background: #ffffff; }
  tbody tr.odd  { background: #f8fafc; }
  tbody td { padding: 7px 12px; border-bottom: 1px solid #f1f5f9; border-right: 1px solid #f1f5f9; font-size: 11px; max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .footer { padding: 12px 24px; font-size: 10px; color: #94a3b8; border-top: 1px solid #e2e8f0; text-align: right; }
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .header { -webkit-print-color-adjust: exact; }
    thead tr { -webkit-print-color-adjust: exact; }
  }
</style>
</head>
<body>
<div class="header">
  <h1>📊 ${reportName || 'Custom Report'}</h1>
  <div class="meta">Generated on ${new Date().toLocaleString('en-IN')} &nbsp;|&nbsp; ${rows.length} record${rows.length !== 1 ? 's' : ''} &nbsp;|&nbsp; ${headers.length} column${headers.length !== 1 ? 's' : ''}</div>
</div>
${filterInfo.length ? `<div class="filters"><label>Active Filters:</label>${filterInfo.join('')}</div>` : ''}
<div class="stats">
  <div class="stat">Total Records: <strong>${rows.length}</strong></div>
  <div class="stat">Columns: <strong>${headers.length}</strong></div>
  <div class="stat">Report: <strong>${reportName || 'Untitled'}</strong></div>
</div>
<table>
  <thead><tr><th>#</th>${headerHtml}</tr></thead>
  <tbody>${rows.map((row, ri) => {
    const cells = headers.map(h => {
      const val = row[h] != null ? String(row[h]) : '—';
      return `<td>${val.length > 60 ? val.slice(0, 60) + '…' : val}</td>`;
    }).join('');
    return `<tr class="${ri % 2 === 0 ? 'even' : 'odd'}"><td style="color:#94a3b8;font-weight:600;text-align:center">${ri + 1}</td>${cells}</tr>`;
  }).join('')}</tbody>
</table>
<div class="footer">Report generated from Privacy Management System &nbsp;|&nbsp; ${new Date().toLocaleDateString('en-IN')}</div>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) { alert('Please allow popups to download PDF'); return; }
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); win.close(); }, 500);
}

/* ── Merge multi-source rows on email/tenant_id ── */
function mergeRowsByKey(allSourceRows, mergeKey = 'email') {
  const map = new Map();
  for (const { alias, rows } of allSourceRows) {
    for (const row of rows) {
      const keyVal = row[mergeKey] || row['tenant_id'] || row['user_id'] || '__unknown__';
      if (!map.has(keyVal)) map.set(keyVal, {});
      const existing = map.get(keyVal);
      for (const [k, v] of Object.entries(row)) {
        existing[`${alias}.${k}`] = v;
        // also store flat for easy access
        if (!existing[k]) existing[k] = v;
      }
    }
  }
  return Array.from(map.values());
}

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
/* Page Guide modal */
function GuideModal({ steps, onClose }) {
  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.6)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', zIndex: 9998,
      }} />
      <div onClick={e => e.stopPropagation()} style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        width: '94vw', maxWidth: 640, maxHeight: '85vh',
        background: '#ffffff', borderRadius: 18,
        boxShadow: '0 32px 80px rgba(2,6,23,0.32), 0 8px 24px rgba(2,6,23,0.12)',
        display: 'flex', flexDirection: 'column', zIndex: 9999, overflow: 'hidden',
        animation: 'modalPopIn 0.28s cubic-bezier(.34,1.56,.64,1)',
      }}>
        <div style={{
          padding: '18px 24px',
          background: 'linear-gradient(135deg,#1e40af 0%,#2563eb 60%,#3b82f6 100%)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>📖</span>
            <span style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>Page Guide</span>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.3)',
            borderRadius: 9, width: 32, height: 32, cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 15, color: '#fff', fontWeight: 700,
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >✕</button>
        </div>

        <div style={{ padding: '18px 24px', overflowY: 'auto' }}>
          {steps.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: 12, padding: '12px 0',
              borderBottom: i < steps.length - 1 ? '1px solid #f1f5f9' : 'none',
            }}>
              <div style={{
                flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
                background: '#eff6ff', color: '#1e40af', fontWeight: 700, fontSize: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{i + 1}</div>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 3 }}>{s.title}</div>
                <div style={{ fontSize: 12.5, color: '#4b5563', lineHeight: 1.5 }}>{s.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
function CustomReport(props) {
  const { onBack, user } = props;
  const cacheKey = createSessionCacheKey('custom-report', user, { tenantId: user?.tenant_id });
  const [cachedReport] = useState(() => readSessionCache(cacheKey));

  const [sources, setSources] = useState(() => cachedReport?.sources || []);
  const [filters, setFilters] = useState(() => cachedReport?.filters || []);
  const [permissions, setPermissions] = useState(() => cachedReport?.permissions || []);
  const [reportName, setReportName] = useState(() => cachedReport?.reportName || '');
  const [expandedNodes, setExpandedNodes] = useState({});
  const [openSections, setOpenSections] = useState({ fields: true, filters: true, perms: false });
  const [fieldSearch, setFieldSearch] = useState({});
  const [loading, setLoading] = useState(false);
  const [reportPageLoading, setReportPageLoading] = useState(false);
  const [reportData, setReportData] = useState(() => cachedReport?.reportData || null);
  const [reportError, setReportError] = useState(null);
  const [reportTimingMs, setReportTimingMs] = useState(null);
  const [reportServerTimingMs, setReportServerTimingMs] = useState(null);
  const [pageOpenMs, setPageOpenMs] = useState(null);
  const [showModal, setShowModal] = useState(() => Boolean(cachedReport?.reportData));
  const [modalPage, setModalPage] = useState(() => cachedReport?.reportData?.page || 1);
  const [modalPageSize, setModalPageSize] = useState(() => cachedReport?.reportData?.pageSize || 20);
  const [guideOpen, setGuideOpen] = useState(false);
  // ── Smart Filters (email + tenant) ──
  const [filterEmail, setFilterEmail] = useState(() => cachedReport?.filterEmail || '');
  const [filterTenant, setFilterTenant] = useState(() => cachedReport?.filterTenant || '');
  const [mergeOnEmail, setMergeOnEmail] = useState(() => cachedReport?.mergeOnEmail ?? true);

  const filterCounter = useRef((cachedReport?.filters || []).reduce((max, filter) => Math.max(max, Number(filter.id) || 0), 0));
  const dragRef = useRef(null);
  const reportPageOpenedAt = useRef(typeof performance !== 'undefined' ? performance.now() : Date.now());
  const reportPayloadRef = useRef(cachedReport?.reportData?.payload || null);
  const reportPageRequestId = useRef(0);

  useEffect(() => {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    setPageOpenMs(Math.round(now - reportPageOpenedAt.current));
  }, []);

  const totalFields = sources.reduce((a, s) => a + s.selectedFields.length, 0);
  const allFilterFields = sources.flatMap(s =>
    (SCHEMA[s.key] || []).map(f => ({ value: s.alias + '.' + f, label: s.label + ' — ' + fmtLabel(f) }))
  );

  function toggleSection(key) { setOpenSections(p => ({ ...p, [key]: !p[key] })); }

  /* ── Sources ── */
  function handleAddSource(e) {
    const key = e.target.value;
    if (!key) return;
    e.target.value = '';
    if (sources.find(s => s.key === key)) return;
    const src = DATA_SOURCES.find(d => d.key === key);
    setSources(p => [...p, { key, alias: 't' + (p.length + 1), label: src.label, icon: src.icon, selectedFields: [], join: null }]);
  }
  function removeSource(key) { setSources(p => p.filter(s => s.key !== key)); }

  /* ── Fields ── */
  function addField(si, field) {
    setSources(p => { const n = [...p]; if (!n[si].selectedFields.includes(field)) n[si] = { ...n[si], selectedFields: [...n[si].selectedFields, field] }; return n; });
  }
  function removeField(si, field) {
    setSources(p => { const n = [...p]; n[si] = { ...n[si], selectedFields: n[si].selectedFields.filter(f => f !== field) }; return n; });
  }
  function selectAllFields(si) {
    setSources(p => { const n = [...p]; n[si] = { ...n[si], selectedFields: [...(SCHEMA[n[si].key] || [])] }; return n; });
  }
  function handleDragStart(si, fi) { dragRef.current = { si, fi }; }
  function handleDropOn(si, fi) {
    const d = dragRef.current; if (!d || d.si !== si) return;
    setSources(p => { const n = [...p]; const sf = [...n[si].selectedFields]; const moved = sf.splice(d.fi, 1)[0]; sf.splice(fi, 0, moved); n[si] = { ...n[si], selectedFields: sf }; dragRef.current = null; return n; });
  }
  function updateJoin(si, prop, val) {
    setSources(p => { const n = [...p]; n[si] = { ...n[si], join: { ...(n[si].join || {}), [prop]: val } }; return n; });
  }

  /* ── Filters ── */
  function addFilter() { filterCounter.current += 1; setFilters(p => [...p, { id: filterCounter.current, field: '', operator: 'contains', value: '', logic: 'AND' }]); }
  function removeFilter(id) { setFilters(p => p.filter(f => f.id !== id)); }
  function updateFilter(id, prop, val) { setFilters(p => p.map(f => f.id === id ? { ...f, [prop]: val } : f)); }
  function toggleLogic(id) { setFilters(p => p.map(f => f.id === id ? { ...f, logic: f.logic === 'AND' ? 'OR' : 'AND' } : f)); }

  /* ── Permissions ── */
  function toggleExpand(key) { setExpandedNodes(p => ({ ...p, [key]: !p[key] })); }
  function isSelected(key) { return permissions.some(p => p.key === key); }
  function togglePerm(key, label, icon, parentKey) {
    if (isSelected(key)) setPermissions(p => p.filter(x => x.key !== key));
    else setPermissions(p => [...p, { key, label, icon, parentKey }]);
  }
  function removePerm(key) { setPermissions(p => p.filter(x => x.key !== key)); }

  /* ── Clear ── */
  function handleClear() {
    setSources([]); setFilters([]); setPermissions([]);
    setReportName(''); setReportData(null); setReportError(null);
    setShowModal(false); setFilterEmail(''); setFilterTenant('');
    reportPayloadRef.current = null;
    setReportTimingMs(null);
    setReportServerTimingMs(null);
    clearSessionCache(cacheKey);
  }

  /* ── Apply smart row-level filters ── */
  function applySmartFilters(rows) {
    let filtered = [...rows];
    if (filterEmail.trim()) {
      const q = filterEmail.trim().toLowerCase();
      filtered = filtered.filter(row =>
        Object.entries(row).some(([k, v]) =>
          k.toLowerCase().includes('email') && String(v || '').toLowerCase().includes(q)
        )
      );
    }
    if (filterTenant.trim()) {
      const q = filterTenant.trim().toLowerCase();
      filtered = filtered.filter(row =>
        Object.entries(row).some(([k, v]) =>
          k.toLowerCase().includes('tenant') && String(v || '').toLowerCase().includes(q)
        )
      );
    }
    return filtered;
  }

  async function loadReportPage(page, pageSize) {
    const basePayload = reportPayloadRef.current || reportData?.payload;
    if (!basePayload) return;
    const requestId = ++reportPageRequestId.current;
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    setReportPageLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/user/reports/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + (localStorage.getItem('token') || ''),
        },
        body: JSON.stringify({ ...basePayload, page, page_size: pageSize }),
      });
      if (!res.ok) throw new Error(`Failed to load report page (${res.status})`);
      const json = await res.json();
      if (requestId !== reportPageRequestId.current) return;
      const columns = json.columns || reportData?.columns || [];
      const rows = normalizeReportRows(json.data || [], columns);
      setReportData({
        ...reportData,
        columns,
        rows,
        total: Number(json.total ?? rows.length),
        page: Number(json.page ?? page),
        pageSize: Number(json.page_size ?? pageSize),
        serverPaginated: true,
        payload: basePayload,
      });
      setModalPage(page);
      setModalPageSize(pageSize);
      setReportTimingMs(Math.round(
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt
      ));
      setReportServerTimingMs(Number(json.timing_ms) || null);
      return true;
    } catch (err) {
      setReportError(err.message || 'Failed to load report page');
      return false;
    } finally {
      if (requestId === reportPageRequestId.current) setReportPageLoading(false);
    }
  }

  async function handleExportCSV() {
    if (!reportData?.rows?.length) { alert('No data to export'); return; }
    if (!reportData.serverPaginated || !reportData.payload) {
      downloadReportCSV(reportData.rows, (reportName || 'report').replace(/\s+/g, '_') + '.csv', reportData.columns);
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/user/reports/export/csv`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + (localStorage.getItem('token') || ''),
        },
        body: JSON.stringify(reportData.payload),
      });
      if (!response.ok) throw new Error(`Failed to export report (${response.status})`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${(reportName || 'report').trim().replace(/\s+/g, '_') || 'report'}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setReportError(err.message || 'Failed to export report');
    }
  }

  /* ── Generate ── */
  async function handleGenerate() {
    if (!sources.length) { alert('Add at least one application.'); return; }
    if (!totalFields) { alert('Select at least one field.'); return; }
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    setLoading(true); setReportData(null); setReportError(null); setShowModal(false);
    setReportTimingMs(null);
    setReportServerTimingMs(null);
    try {
      try { window.showLoading('Generating report…'); } catch (e) { }
      const tenantId = localStorage.getItem('tenant_id') || null;

      // Build smart filters: add email/tenant filters automatically if set
      const smartFilters = [...filters];
      if (filterEmail.trim()) {
        const emailFields = sources.flatMap(s =>
          (SCHEMA[s.key] || []).filter(f => f.includes('email')).map(f => s.alias + '.' + f)
        );
        if (emailFields.length) smartFilters.push({ field: emailFields[0], operator: 'contains', value: filterEmail.trim(), logic: 'AND' });
      }
      if (filterTenant.trim()) {
        const tenantFields = sources.flatMap(s =>
          (SCHEMA[s.key] || []).filter(f => f.includes('tenant')).map(f => s.alias + '.' + f)
        );
        if (tenantFields.length) smartFilters.push({ field: tenantFields[0], operator: 'equals', value: filterTenant.trim(), logic: 'AND' });
      }

      const payload = {
        sources: sources.map(s => ({ key: s.key, alias: s.alias, fields: s.selectedFields, join: s.join || null })),
        filters: smartFilters, tenant_id: tenantId
      };

      let columns = [], rows = [];
      let resultTotal = null;
      const useServerPagination = !(sources.length > 1 && mergeOnEmail);
      const requestPayload = useServerPagination
        ? { ...payload, page: 1, page_size: 20 }
        : payload;
      reportPayloadRef.current = useServerPagination ? payload : null;

      if (sources.length > 1 && mergeOnEmail) {
        // Fetch each source individually, then merge on email/tenant
        const allSourceRows = await Promise.all(sources.map(async s => {
          const sPayload = {
            sources: [{ key: s.key, alias: s.alias, fields: s.selectedFields, join: null }],
            filters: smartFilters, tenant_id: tenantId
          };
          const res = await fetch(`${API_BASE_URL}/user/reports/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (localStorage.getItem('token') || '') },
            body: JSON.stringify(sPayload)
          });
          if (!res.ok) throw new Error(`Failed to fetch ${s.label} report (${res.status})`);
          const json = await res.json();
          return {
            alias: s.alias,
            key: s.key,
            rows: Array.isArray(json.data) ? json.data : [],
          };
        }));

        // Merge all source rows by email/tenant into single rows
        const merged = mergeRowsByKey(allSourceRows, 'email');
        const filteredMerged = applySmartFilters(merged);
        columns = sources.flatMap(s => s.selectedFields.map(f => s.alias + '.' + f));
        rows = filteredMerged;
        resultTotal = rows.length;
      } else {
        // Single source or no merge
        const res = await fetch(`${API_BASE_URL}/user/reports/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + (localStorage.getItem('token') || '') },
          body: JSON.stringify(requestPayload)
        });
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        const json = await res.json();
        setReportServerTimingMs(Number(json.timing_ms) || null);
        columns = (json.columns && json.columns.length)
          ? json.columns
          : sources.flatMap(s => s.selectedFields.map(f => s.alias + '.' + f));
        rows = json.data || [];
        resultTotal = Number(json.total ?? rows.length);
      }

      rows = normalizeReportRows(rows, columns);
      const normalizedData = {
        columns,
        rows,
        total: resultTotal ?? rows.length,
        page: 1,
        pageSize: 20,
        serverPaginated: useServerPagination,
        payload: useServerPagination ? payload : null,
      };
      setModalPage(1);
      setModalPageSize(20);
      setReportData(normalizedData);
      setReportTimingMs(Math.round(
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startedAt
      ));
      setShowModal(true);
      writeSessionCache(cacheKey, {
        sources,
        filters,
        permissions,
        reportName,
        filterEmail,
        filterTenant,
        mergeOnEmail,
        reportData: normalizedData,
      });
    } catch (err) {
      setReportError(err.message || 'Failed to generate report');
    } finally {
      setLoading(false);
      try { window.hideLoading(); } catch (e) { }
    }
  }

  /* ══════════════════════════════════════════════════════
     REPORT MODAL
  ══════════════════════════════════════════════════════ */
  const ReportModal = () => {
    if (!showModal || !reportData) return null;
    const { columns: cols, rows } = reportData;
    const remotePagination = Boolean(reportData.serverPaginated && reportData.payload);
    const totalItems = Number(reportData.total ?? rows.length);
    const totalPages = Math.ceil(totalItems / modalPageSize) || 1;
    const startIdx = (modalPage - 1) * modalPageSize;
    const currentRows = remotePagination ? rows : rows.slice(startIdx, startIdx + modalPageSize);

    const changePage = async (page) => {
      if (remotePagination) {
        const loaded = await loadReportPage(page, modalPageSize);
        if (loaded) setModalPage(page);
        return;
      }
      setModalPage(page);
    };
    const changePageSize = async (size) => {
      if (remotePagination) {
        const loaded = await loadReportPage(1, size);
        if (loaded) {
          setModalPageSize(size);
          setModalPage(1);
        }
        return;
      }
      setModalPageSize(size);
      setModalPage(1);
    };

    return (
      <>
        <div onClick={() => setShowModal(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.6)',
          backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', zIndex: 9998,
        }} />
        <div onClick={e => e.stopPropagation()} style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: '96vw', maxWidth: 1200, maxHeight: '92vh',
          background: '#ffffff', borderRadius: 18,
          boxShadow: '0 32px 80px rgba(2,6,23,0.32), 0 8px 24px rgba(2,6,23,0.12)',
          display: 'flex', flexDirection: 'column', zIndex: 9999, overflow: 'hidden',
          animation: 'modalPopIn 0.28s cubic-bezier(.34,1.56,.64,1)',
        }}>
          {/* Header */}
          <div style={{
            padding: '18px 24px',
            background: 'linear-gradient(135deg,#1e40af 0%,#2563eb 60%,#3b82f6 100%)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ fontSize: 24 }}>📊</div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{reportName || 'Report Results'}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
                  {totalItems.toLocaleString()} records · {cols.length} columns
                  {reportTimingMs !== null && <span style={{ marginLeft: 8 }}>Data shown in {reportTimingMs} ms</span>}
                  {reportServerTimingMs !== null && <span style={{ marginLeft: 8 }}>Server: {reportServerTimingMs} ms</span>}
                  {filterEmail && <span style={{ marginLeft: 8, background: 'rgba(255,255,255,0.2)', borderRadius: 20, padding: '1px 8px' }}>📧 {filterEmail}</span>}
                  {filterTenant && <span style={{ marginLeft: 6, background: 'rgba(255,255,255,0.2)', borderRadius: 20, padding: '1px 8px' }}>🏢 {filterTenant}</span>}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button
                onClick={handleExportCSV}
                style={{
                  padding: '8px 16px', background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.35)',
                  borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#fff',
                  display: 'flex', alignItems: 'center', gap: 7, transition: 'all 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.28)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              >
                <span>⬇</span> CSV
              </button>
              {/* <button
                onClick={() => downloadReportPDF(rows, cols, reportName, filterEmail, filterTenant)}
                style={{padding:'8px 16px',background:'#fff',border:'none',
                  borderRadius:9,fontSize:13,fontWeight:700,cursor:'pointer',color:'#1e40af',
                  display:'flex',alignItems:'center',gap:7,transition:'all 0.15s',}}
                onMouseEnter={e=>{e.currentTarget.style.background='#eff6ff';e.currentTarget.style.transform='translateY(-1px)';}}
                onMouseLeave={e=>{e.currentTarget.style.background='#fff';e.currentTarget.style.transform='translateY(0)';}}
              >
                <span>🖨️</span> PDF
              </button> */}
              <button onClick={() => setShowModal(false)} style={{
                background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.3)',
                borderRadius: 9, width: 36, height: 36, cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: 16, color: '#fff', fontWeight: 700,
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              >✕</button>
            </div>
          </div>

          {/* Filter Summary Bar */}
          {(filterEmail || filterTenant) && (
            <div style={{ padding: '8px 24px', background: '#eff6ff', borderBottom: '1px solid #bfdbfe', display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>Active Filters:</span>
              {filterEmail && <span style={{ background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 20, padding: '3px 12px', fontSize: 12, color: '#1d4ed8' }}>📧 {filterEmail}</span>}
              {filterTenant && <span style={{ background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 20, padding: '3px 12px', fontSize: 12, color: '#1d4ed8' }}>🏢 Tenant: {filterTenant}</span>}
              <span style={{ fontSize: 12, color: '#64748b', marginLeft: 'auto' }}>{totalItems.toLocaleString()} matching records</span>
            </div>
          )}

          {/* Table */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            {rows.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 60, color: '#94a3b8' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#475569', marginBottom: 8 }}>No records found</div>
                <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center' }}>
                  {(filterEmail || filterTenant) ? 'No data matches the applied email/tenant filters. Try adjusting them.' : 'No data found for the selected fields and filters.'}
                </div>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, fontFamily: 'inherit' }}>
                <thead>
                  <tr style={{ background: '#1e3a5f', position: 'sticky', top: 0, zIndex: 2 }}>
                    <th style={{
                      padding: '10px 14px', textAlign: 'center', fontWeight: 700, fontSize: 11, color: '#94a3b8',
                      borderBottom: '2px solid #1e40af', borderRight: '1px solid rgba(255,255,255,0.08)',
                      whiteSpace: 'nowrap', width: 44, background: '#1e3a5f'
                    }}>#</th>
                    {cols.map((col, i) => {
                      const parts = col.includes('.') ? col.split('.') : [null, col];
                      const alias = parts[0];
                      const field = parts.slice(1).join('.');
                      const src = sources.find(s => s.alias === alias);
                      return (
                        <th key={i} style={{
                          padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 10.5,
                          color: '#e2e8f0', borderBottom: '2px solid #1e40af',
                          borderRight: '1px solid rgba(255,255,255,0.08)', whiteSpace: 'nowrap',
                          textTransform: 'uppercase', letterSpacing: '0.05em', background: '#1e3a5f'
                        }}>
                          {fmtLabel(field)}
                          {src && <div style={{ fontSize: 9, color: '#60a5fa', fontWeight: 500, textTransform: 'none', letterSpacing: 0, marginTop: 2 }}>
                            {src.icon} {src.label}
                          </div>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {currentRows.map((row, ri) => (
                    <tr key={ri}
                      style={{ background: ri % 2 === 0 ? '#ffffff' : '#f8fafc' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                      onMouseLeave={e => e.currentTarget.style.background = ri % 2 === 0 ? '#ffffff' : '#f8fafc'}
                    >
                      <td style={{
                        padding: '9px 14px', textAlign: 'center', fontSize: 11, color: '#94a3b8',
                        fontWeight: 600, borderBottom: '1px solid #f1f5f9', borderRight: '1px solid #f1f5f9'
                      }}>{startIdx + ri + 1}</td>
                      {cols.map((col, ci) => {
                        const val = row[col] != null ? String(row[col]) : (row[col.split('.').slice(1).join('.')] != null ? String(row[col.split('.').slice(1).join('.')]) : '');
                        const isEmail = col.toLowerCase().includes('email');
                        const isEmpty = !val;
                        return (
                          <td key={ci} title={val} style={{
                            padding: '9px 14px', fontSize: 12.5,
                            color: isEmpty ? '#cbd5e1' : isEmail ? '#2563eb' : '#1e293b',
                            fontWeight: isEmail ? 600 : 400,
                            borderBottom: '1px solid #f1f5f9', borderRight: '1px solid #f1f5f9',
                            maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {val ? (val.length > 60 ? val.slice(0, 60) + '…' : val) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <CommonPagination
            currentPage={modalPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={modalPageSize}
            onPageChange={changePage}
            onPageSizeChange={changePageSize}
            loading={reportPageLoading}
          />
        </div>
        <style>{`
          @keyframes modalPopIn {
            from{opacity:0;transform:translate(-50%,-48%) scale(0.94);}
            to{opacity:1;transform:translate(-50%,-50%) scale(1);}
          }
        `}</style>
      </>
    );
  };

  /* ══════════════ TABLE STYLES ══════════════ */
  const thStyle = {
    padding: '9px 14px', textAlign: 'left', fontWeight: 700,
    fontSize: 11.5, color: 'var(--text2,#64748b)', whiteSpace: 'nowrap',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    borderRight: '1px solid var(--border,#e2e8f0)',
  };
  const tdStyle = {
    padding: '8px 14px', color: 'var(--text,#1e293b)', fontSize: 12.5,
    borderRight: '1px solid var(--border,#e2e8f0)', maxWidth: 220,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };

  /* ══════════════ RENDER ══════════════ */
  return (
    <div className="cr-root">
      <ReportModal />
      {guideOpen && (
        <GuideModal
          steps={GUIDE_STEPS}
          onClose={() => setGuideOpen(false)}
        />
      )}

      <div className="cr-body">

        {/* ── SIDEBAR ── */}
        <aside className="cr-sidebar">
          <div className="cr-sb-section">
            <div className="cr-sb-label">🗂 Applications</div>
            <div className="cr-sb-select-wrap tour-target-add-app">
              <select className="cr-sb-select" onChange={handleAddSource} defaultValue="">
                <option value="">Select Application</option>
                {DATA_SOURCES.filter(d => !sources.find(s => s.key === d.key)).map(d =>
                  <option key={d.key} value={d.key}>{d.icon} {d.label}</option>
                )}
              </select>
              <span className="cr-sb-arrow">▼</span>
            </div>
            <div className="cr-app-tags">
              {sources.length === 0
                ? <div className="cr-app-empty">No applications added yet</div>
                : sources.map(s => (
                  <div key={s.key} className="cr-app-tag">
                    <div className="cr-app-tag-left">
                      <div className="cr-app-dot" />
                      {s.icon} {s.label}
                      <span className="cr-app-alias">{s.alias}</span>
                    </div>
                    <button className="cr-btn-rm-app" onClick={() => removeSource(s.key)}>✕</button>
                  </div>
                ))
              }
            </div>
          </div>

          {/* ── Smart Filter Box ── */}
          <div style={{
            margin: '12px 0', padding: '14px',
            background: 'linear-gradient(135deg,#eff6ff,#f0fdf4)',
            border: '1.5px solid #bfdbfe', borderRadius: 12,
          }}>
            <div className="tour-target-smart-filters" style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>🎯</span> Smart Filters
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                📧 Filter by Email
              </label>
              <input
                type="email"
                value={filterEmail}
                onChange={e => setFilterEmail(e.target.value)}
                placeholder="user@example.com"
                style={{
                  width: '100%', padding: '8px 10px', border: '1.5px solid #bfdbfe',
                  borderRadius: 8, fontSize: 12, outline: 'none', boxSizing: 'border-box',
                  background: '#fff', color: '#1e293b'
                }}
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e => e.target.style.borderColor = '#bfdbfe'}
              />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                🏢 Filter by Tenant ID
              </label>
              <input
                type="text"
                value={filterTenant}
                onChange={e => setFilterTenant(e.target.value)}
                placeholder="Tenant ID"
                style={{
                  width: '100%', padding: '8px 10px', border: '1.5px solid #bfdbfe',
                  borderRadius: 8, fontSize: 12, outline: 'none', boxSizing: 'border-box',
                  background: '#fff', color: '#1e293b'
                }}
                onFocus={e => e.target.style.borderColor = '#2563eb'}
                onBlur={e => e.target.style.borderColor = '#bfdbfe'}
              />
            </div>

            {sources.length > 1 && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#374151' }}>
                <input type="checkbox" checked={mergeOnEmail} onChange={e => setMergeOnEmail(e.target.checked)}
                  style={{ accentColor: '#2563eb', width: 14, height: 14 }} />
                <span>Merge sources by email into single rows</span>
              </label>
            )}

            {(filterEmail || filterTenant) && (
              <div style={{ marginTop: 10, padding: '6px 10px', background: '#dbeafe', borderRadius: 8, fontSize: 11, color: '#1d4ed8' }}>
                ✓ Filters active — results will be scoped to matching records
              </div>
            )}
          </div>

          <hr className="cr-sb-divider" />

          <div className="cr-sb-section">
            <div className="cr-sb-label">📄 Report Name</div>
            <input className="cr-sb-input" placeholder="Enter Report Name" value={reportName} onChange={e => setReportName(e.target.value)} />
          </div>

          <div className="cr-sb-summary">
            <div className="cr-summary-chip">Sources <span className="cr-summary-num">{sources.length}</span></div>
            <div className="cr-summary-chip">Fields  <span className="cr-summary-num">{totalFields}</span></div>
            <div className="cr-summary-chip">Filters <span className="cr-summary-num">{filters.length + (filterEmail ? 1 : 0) + (filterTenant ? 1 : 0)}</span></div>
            <div className="cr-summary-chip">Perms   <span className="cr-summary-num">{permissions.length}</span></div>
          </div>

          <hr className="cr-sb-divider" />
        </aside>

        {/* ── MAIN ── */}
        <main className="cr-main">

          <div className="cr-page-header">
            <div>
              <div className="cr-page-title">Build Custom Report</div>
              <div className="cr-page-sub">
                Combine data sources, apply filters, and export as CSV or PDF.
                {pageOpenMs !== null && <span style={{ marginLeft: 8 }}>Page opened in {pageOpenMs} ms</span>}
                {reportTimingMs !== null && <span style={{ marginLeft: 8 }}>Data shown in {reportTimingMs} ms</span>}
                {sources.length > 1 && mergeOnEmail && <span style={{ marginLeft: 8, color: '#7c3aed', fontWeight: 600, fontSize: 11 }}>✦ Multi-source merge active</span>}
              </div>
            </div>
            <div className="cr-header-actions">
              <button className="cr-btn cr-btn-outline cr-btn-icon" onClick={onBack}>←</button>
              <button className="cr-btn cr-btn-primary" onClick={handleGenerate} disabled={loading}>
                {loading ? '⏳ Loading…' : '✓ Generate Report'}
              </button>
              <button className="cr-btn cr-btn-outline" onClick={() => setGuideOpen(true)}>
                📖 Page Guide
              </button>
            </div>
          </div>

          {/* ── Section 1: Choose Fields ── */}
          <div className="cr-section">
            <div className={'cr-sec-header tour-target-fields-section' + (openSections.fields ? ' open' : '')} onClick={() => toggleSection('fields')}>
              <div className="cr-sec-header-left">
                <div className="cr-sec-icon cr-icon-fields">📋</div>
                <div>
                  <div className="cr-sec-title">Choose Fields</div>
                  <div className="cr-sec-sub">Select and drag to reorder columns in your report</div>
                </div>
              </div>
              <div className="cr-sec-header-right">
                <span className="cr-sec-badge">{totalFields} selected</span>
                <span className={'cr-sec-arrow ' + (openSections.fields ? 'open' : 'closed')}>▼</span>
              </div>
            </div>

            {openSections.fields && (
              <div className="cr-sec-body">
                {sources.length === 0 ? (
                  <div className="cr-no-source">
                    <div className="cr-no-source-icon">📂</div>
                    Add an application from the sidebar to start selecting fields.
                  </div>
                ) : sources.map((s, si) => {
                  const availQ = fieldSearch['a' + si] || '';
                  const selQ = fieldSearch['s' + si] || '';
                  const allFields = SCHEMA[s.key] || [];
                  const available = allFields.filter(f => !s.selectedFields.includes(f) && fmtLabel(f).toLowerCase().includes(availQ.toLowerCase()));
                  const selected = s.selectedFields.filter(f => fmtLabel(f).toLowerCase().includes(selQ.toLowerCase()));
                  return (
                    <div key={s.key} className="cr-fields-block">
                      {si > 0 && (
                        <div className="cr-join-row">
                          <span className="cr-join-lbl">JOIN</span>
                          <select className="cr-join-select" defaultValue="LEFT" onChange={e => updateJoin(si, 'type', e.target.value)}>
                            {['LEFT', 'INNER', 'RIGHT', 'FULL'].map(t => <option key={t}>{t}</option>)}
                          </select>
                          <span style={{ fontSize: 11, color: 'var(--muted,#94a3b8)' }}>ON</span>
                          <input className="cr-join-on-input" placeholder="e.g. t1.id = t2.consent_id" onChange={e => updateJoin(si, 'on', e.target.value)} />
                        </div>
                      )}
                      <div className="cr-fields-block-title">
                        {s.icon} {s.label} Fields <span>{s.alias}</span>
                      </div>
                      <div className="cr-fields-pane">
                        <div className="cr-fields-col">
                          <div className="cr-fields-col-head">
                            <span className="cr-fields-col-title">Available Fields</span>
                            <input className="cr-fields-search" placeholder="Search…" value={availQ}
                              onChange={e => setFieldSearch(p => ({ ...p, ['a' + si]: e.target.value }))} />
                            <button className="cr-btn cr-btn-ghost cr-btn-sm" onClick={() => selectAllFields(si)}>All</button>
                          </div>
                          <div className="cr-fields-list">
                            {available.length === 0
                              ? <div className="cr-fields-empty"><span className="cr-fields-empty-icon">✓</span>All fields selected</div>
                              : available.map(f => (
                                <div key={f} className="cr-field-avail" onClick={() => addField(si, f)}>
                                  {fmtLabel(f)}<span className="cr-field-avail-icon">＋</span>
                                </div>
                              ))
                            }
                          </div>
                        </div>
                        <div className="cr-fields-col">
                          <div className="cr-fields-col-head">
                            <span className="cr-fields-col-title">Selected</span>
                            <input className="cr-fields-search" placeholder="Search…" value={selQ}
                              onChange={e => setFieldSearch(p => ({ ...p, ['s' + si]: e.target.value }))} />
                            <span className="cr-fields-count">{s.selectedFields.length}</span>
                          </div>
                          <div className="cr-fields-list" onDragOver={e => e.preventDefault()}>
                            {selected.length === 0
                              ? <div className="cr-fields-empty"><span className="cr-fields-empty-icon">←</span>Click fields to add</div>
                              : selected.map(f => {
                                const realFi = s.selectedFields.indexOf(f);
                                return (
                                  <div key={f} className="cr-field-sel" draggable
                                    onDragStart={() => handleDragStart(si, realFi)}
                                    onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('drag-over'); }}
                                    onDragLeave={e => e.currentTarget.classList.remove('drag-over')}
                                    onDrop={e => { e.currentTarget.classList.remove('drag-over'); handleDropOn(si, realFi); }}>
                                    <div className="cr-field-sel-left">
                                      <span className="cr-drag-handle">⋮⋮</span>{fmtLabel(f)}
                                    </div>
                                    <button className="cr-btn-rm-field" onClick={e => { e.stopPropagation(); removeField(si, f); }}>✕</button>
                                  </div>
                                );
                              })
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Section 2: Add Filters ── */}
          <div className="cr-section">
            <div className={'cr-sec-header tour-target-filters-section' + (openSections.filters ? ' open' : '')} onClick={() => toggleSection('filters')}>
              <div className="cr-sec-header-left">
                <div className="cr-sec-icon cr-icon-filters">🔍</div>
                <div>
                  <div className="cr-sec-title">Advanced Filters</div>
                  <div className="cr-sec-sub">Additional field-level conditions on top of smart filters</div>
                </div>
              </div>
              <div className="cr-sec-header-right">
                <span className="cr-sec-badge">{filters.length + (filterEmail ? 1 : 0) + (filterTenant ? 1 : 0)} active</span>
                <span className={'cr-sec-arrow ' + (openSections.filters ? 'open' : 'closed')}>▼</span>
              </div>
            </div>
            {openSections.filters && (
              <div className="cr-sec-body">
                {/* Smart filter summary */}
                {(filterEmail || filterTenant) && (
                  <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#1d4ed8', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700 }}>🎯 Smart Filters:</span>
                    {filterEmail && <span>📧 Email contains "<strong>{filterEmail}</strong>"</span>}
                    {filterTenant && <span>🏢 Tenant ID = "<strong>{filterTenant}</strong>"</span>}
                  </div>
                )}
                {filters.length === 0 ? (
                  <div className="cr-filters-empty">No additional conditions added. Click below to add field-level filters.</div>
                ) : (
                  <div className="cr-filters-list">
                    {filters.map((f, i) => (
                      <React.Fragment key={f.id}>
                        {i > 0 && (
                          <div className="cr-logic-divider">
                            <button className={'cr-filter-logic cr-logic-' + (f.logic === 'AND' ? 'and' : 'or')} onClick={() => toggleLogic(f.id)}>
                              {f.logic}
                            </button>
                          </div>
                        )}
                        <div className="cr-filter-row">
                          <select className="cr-filter-select cr-filter-field" value={f.field} onChange={e => updateFilter(f.id, 'field', e.target.value)}>
                            <option value="">— Select Field —</option>
                            {allFilterFields.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          <select className="cr-filter-select cr-filter-op" value={f.operator} onChange={e => updateFilter(f.id, 'operator', e.target.value)}>
                            {OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          <input className="cr-filter-input" placeholder="Value…" value={f.value} onChange={e => updateFilter(f.id, 'value', e.target.value)} />
                          <button className="cr-btn-rm-filter" onClick={() => removeFilter(f.id)}>✕</button>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                )}
                <button className="cr-btn cr-btn-ghost cr-btn-sm tour-target-add-condition" onClick={addFilter}>＋ Add Condition</button>
              </div>
            )}
          </div>

          {/* ── Section 3: Permissions ── */}
          <div className="cr-section">
            <div className={'cr-sec-header tour-target-perms-section' + (openSections.perms ? ' open' : '')} onClick={() => toggleSection('perms')}>
              <div className="cr-sec-header-left">
                <div className="cr-sec-icon cr-icon-perms">🔐</div>
                <div>
                  <div className="cr-sec-title">Report Permissions</div>
                  <div className="cr-sec-sub">Configure who can access this report</div>
                </div>
              </div>
              <div className="cr-sec-header-right">
                <span className="cr-sec-badge">{permissions.length} selected</span>
                <span className={'cr-sec-arrow ' + (openSections.perms ? 'open' : 'closed')}>▼</span>
              </div>
            </div>
            {openSections.perms && (
              <div className="cr-sec-body">
                <div className="cr-perms-pane">
                  <div className="cr-perms-col">
                    <div className="cr-perms-col-head">
                      <span>✓</span>
                      <span className="cr-perms-col-title">Select Permission</span>
                    </div>
                    <div className="cr-perm-tree">
                      {PERM_TREE.map(node => (
                        <React.Fragment key={node.key}>
                          <div className={'cr-tree-item' + (isSelected(node.key) ? ' selected' : '')}
                            onClick={() => { node.children.length === 0 ? togglePerm(node.key, node.label, node.icon, null) : toggleExpand(node.key); }}>
                            <span className="cr-tree-icon">{node.icon}</span>
                            {node.label}
                            {node.children.length > 0 && <span className={'cr-tree-expand' + (expandedNodes[node.key] ? ' open' : '')}>▶</span>}
                          </div>
                          {node.children.length > 0 && expandedNodes[node.key] && (
                            <div className="cr-tree-children">
                              {node.children.map(child => (
                                <div key={child.key} className={'cr-tree-child' + (isSelected(child.key) ? ' selected' : '')}
                                  onClick={() => togglePerm(child.key, child.label, child.icon, node.key)}>
                                  <div className="cr-child-check">{isSelected(child.key) ? '✓' : ''}</div>
                                  <span>{child.icon}</span>{child.label}
                                </div>
                              ))}
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  <div className="cr-perms-col">
                    <div className="cr-perms-col-head">
                      <span>📋</span>
                      <span className="cr-perms-col-title">Selected Permissions</span>
                      <span className="cr-perms-count">{permissions.length}</span>
                    </div>
                    <div className="cr-perms-sel-list">
                      {permissions.length === 0
                        ? <div className="cr-perms-empty">
                          <div className="cr-perms-empty-icon">🔒</div>
                          No permissions selected.<br />Choose from the left panel.
                        </div>
                        : permissions.map(p => (
                          <div key={p.key} className="cr-perm-tag">
                            <div className="cr-perm-tag-left">
                              <span>{p.icon}</span>{p.label}
                              <span className="cr-perm-type-badge">
                                {p.parentKey ? (TYPE_MAP[p.parentKey] || p.parentKey.toUpperCase()) : (TYPE_MAP[p.key] || 'ALL')}
                              </span>
                            </div>
                            <button className="cr-btn-rm-perm" onClick={() => removePerm(p.key)}>✕</button>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--muted,#94a3b8)', fontSize: 14 }}>
              <div style={{ fontSize: 32, marginBottom: 12, animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</div>
              <div>Fetching and merging data, please wait…</div>
            </div>
          )}

          {/* Error */}
          {reportError && (
            <div style={{
              background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 10,
              padding: '16px 20px', marginTop: 16, color: '#dc2626', fontSize: 13, fontWeight: 600
            }}>
              ❌ {reportError}
            </div>
          )}

          {/* Inline Results Preview */}
          {reportData && !loading && (
            <div className="cr-section" style={{ marginTop: 16 }}>
              <div className="cr-sec-header open" style={{ cursor: 'default' }}>
                <div className="cr-sec-header-left">
                  <div className="cr-sec-icon" style={{ background: 'linear-gradient(135deg,#8b5cf6,#6d28d9)' }}>📊</div>
                  <div>
                    <div className="cr-sec-title">{reportName || 'Report Results'}</div>
                    <div className="cr-sec-sub">
                      {Number(reportData.total ?? reportData.rows.length).toLocaleString()} row{Number(reportData.total ?? reportData.rows.length) !== 1 ? 's' : ''} · {reportData.columns.length} column{reportData.columns.length !== 1 ? 's' : ''}
                      {sources.length > 1 && mergeOnEmail && <span style={{ marginLeft: 8, color: '#7c3aed', fontWeight: 700 }}>✦ Merged by email</span>}
                    </div>
                  </div>
                </div>
                <div className="cr-sec-header-right" style={{ gap: 8 }}>
                  <button className="cr-btn cr-btn-outline cr-btn-sm"
                    onClick={handleExportCSV}>
                    ⬇ CSV
                  </button>
                  {/* <button className="cr-btn cr-btn-primary cr-btn-sm"
                    onClick={()=>downloadReportPDF(reportData.rows,reportData.columns,reportName,filterEmail,filterTenant)}
                    style={{background:'linear-gradient(135deg,#7c3aed,#6d28d9)',border:'none',color:'#fff',padding:'6px 14px',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer'}}>
                    🖨️ PDF
                  </button> */}
                  <button className="cr-btn cr-btn-outline cr-btn-sm" onClick={() => setShowModal(true)}>
                    ⛶ Full View
                  </button>
                </div>
              </div>
              <div className="cr-sec-body" style={{ padding: 0, overflowX: 'auto' }}>
                {reportData.rows.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px', color: 'var(--muted,#94a3b8)', fontSize: 13 }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
                    No data found for the selected fields and filters.
                    {(filterEmail || filterTenant) && <div style={{ fontSize: 12, marginTop: 6, color: '#f59e0b' }}>Try clearing the Smart Filters in the sidebar.</div>}
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, fontFamily: 'var(--font,inherit)' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg,#f1f5f9)', borderBottom: '2px solid var(--border,#e2e8f0)' }}>
                        <th style={thStyle}>#</th>
                        {reportData.columns.map(col => (
                          <th key={col} style={thStyle}>
                            {fmtLabel(col.includes('.') ? col.split('.').slice(1).join('.') : col)}
                            <div style={{ fontSize: 9.5, color: 'var(--muted,#94a3b8)', fontWeight: 500, marginTop: 1 }}>
                              {col.split('.')[0]}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.rows.slice(0, 20).map((row, ri) => (
                        <tr key={ri} style={{ borderBottom: '1px solid var(--border,#e2e8f0)', background: ri % 2 === 0 ? 'var(--surface,#fff)' : 'var(--bg,#f8fafc)' }}>
                          <td style={tdStyle}>{(reportData.serverPaginated ? (Number(reportData.page || 1) - 1) * Number(reportData.pageSize || 20) : 0) + ri + 1}</td>
                          {reportData.columns.map(col => {
                            const val = row[col];
                            return (
                              <td key={col} style={tdStyle}>
                                {val != null && String(val).length > 60 ? String(val).slice(0, 60) + '…' : (val != null ? String(val) : '—')}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {Number(reportData.total ?? reportData.rows.length) > 20 && (
                  <div style={{ padding: '10px 16px', fontSize: 12, color: '#64748b', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Showing {reportData.rows.length} of <strong>{Number(reportData.total ?? reportData.rows.length).toLocaleString()}</strong> records</span>
                    <button onClick={() => setShowModal(true)}
                      style={{ fontSize: 12, color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>
                      View all {Number(reportData.total ?? reportData.rows.length).toLocaleString()} records →
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

        </main>
      </div>

      {/* ── BOTTOM ACTION BAR ── */}
      <div className="cr-action-bar">
        <div className="cr-ab-actions">
          <button className="cr-btn cr-btn-outline" onClick={handleClear}>Clear All</button>
          {reportData && (
            <>
              <button className="cr-btn cr-btn-outline"
                onClick={handleExportCSV}>
                ⬇ Export CSV
              </button>
              {/* <button
                onClick={()=>downloadReportPDF(reportData.rows,reportData.columns,reportName,filterEmail,filterTenant)}
                style={{padding:'10px 20px',background:'linear-gradient(135deg,#7c3aed,#6d28d9)',color:'#fff',border:'none',
                  borderRadius:9,fontSize:14,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',gap:8}}>
                🖨️ Download PDF
              </button> */}
            </>
          )}
          <button className="cr-btn cr-btn-primary" onClick={handleGenerate} disabled={loading}>
            {loading ? '⏳ Loading…' : '✓ Generate Report'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default CustomReport;